import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import '../styles/PreviewChain.css';

export default function PreviewChainPage() {
  const navigate = useNavigate();
  const { shortCode } = useParams();
  const [chainData, setChainData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadChainData = () => {
      // Try to load from sessionStorage first
      const storedData = sessionStorage.getItem('pendingChainData');
      
      if (storedData) {
        try {
          const data = JSON.parse(storedData);
          setChainData(data);
        } catch (error) {
          console.error('Failed to parse chain data:', error);
        }
      }
      
      setIsLoading(false);
    };

    loadChainData();
  }, [shortCode]);

  const handleEdit = () => {
    navigate('/create-chain');
  };

  const handleContinue = () => {
    navigate(`/auth?redirect=/claim/${shortCode}`);
  };

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="preview-card">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading preview...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!chainData) {
    return (
      <div className="page-container">
        <div className="preview-card">
          <div className="error-state">
            <h2>Preview Not Available</h2>
            <p>Unable to load chain preview. Please try creating a new chain.</p>
            <button onClick={() => navigate('/create-chain')} className="primary-button">
              Create New Chain
            </button>
          </div>
        </div>
      </div>
    );
  }

  const chainTypeDisplay = chainData.chainType === 'tehillim' ? 'Tehillim' : 'Challah';
  const chainTypeIcon = chainData.chainType === 'tehillim' ? '📖' : '🍞';
  const maxProgress = chainData.chainType === 'tehillim' ? 150 : 52;
  
  // Format goal date
  const goalDateFormatted = chainData.goalDate 
    ? new Date(chainData.goalDate).toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      })
    : 'Not set';

  return (
    <div className="page-container">
      <div className="preview-card">
        {/* Header */}
        <div className="preview-header">
          <div className="header-icon">✨</div>
          <h1 className="preview-title">Preview Your Chain</h1>
          <p className="preview-subtitle">This is how your prayer chain will appear to others</p>
        </div>

        {/* Prayer Card Preview */}
        <div className="prayer-card">
          <div className="card-image-container">
            {chainData.imagePreviewUrl ? (
              <img 
                src={chainData.imagePreviewUrl} 
                alt="Prayer card background" 
                className="card-image"
              />
            ) : (
              <div className="card-image placeholder-image">
                <div className="placeholder-icon">{chainTypeIcon}</div>
              </div>
            )}
            <div className="card-overlay"></div>
          </div>
          
          <div className="card-content">
            {/* Chain Type Badge */}
            <div className="chain-type-badge">
              <span className="badge-icon">{chainTypeIcon}</span>
              <span className="badge-text">{chainTypeDisplay}</span>
            </div>
            
            {/* Recipient Name */}
            <h2 className="recipient-name" dir="rtl">{chainData.recipientName}</h2>
            
            {/* Stats Grid */}
            <div className="stats-grid">
              <div className="stat-item">
                <span className="stat-icon">🎯</span>
                <div className="stat-content">
                  <span className="stat-label">Goal Date</span>
                  <span className="stat-value">{goalDateFormatted}</span>
                </div>
              </div>
              
              <div className="stat-item">
                <span className="stat-icon">👥</span>
                <div className="stat-content">
                  <span className="stat-label">Participants</span>
                  <span className="stat-value">0</span>
                </div>
              </div>
              
              <div className="stat-item">
                <span className="stat-icon">📊</span>
                <div className="stat-content">
                  <span className="stat-label">Progress</span>
                  <span className="stat-value">0/{maxProgress}</span>
                </div>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="progress-bar-container">
              <div className="progress-bar">
                <div className="progress-fill" style={{width: '0%'}}></div>
              </div>
              <span className="progress-text">0% Complete</span>
            </div>
          </div>
        </div>

        {/* Description Section */}
        {chainData.description && (
          <div className="description-section">
            <h3 className="description-title">Description</h3>
            <p className="description-text">{chainData.description}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="action-buttons">
          <button type="button" className="secondary-button" onClick={handleEdit}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <line x1="19" y1="12" x2="5" y2="12" strokeWidth="2"/>
              <polyline points="12 19 5 12 12 5" strokeWidth="2"/>
            </svg>
            <span>Edit</span>
          </button>
          
          <button type="button" className="primary-button" onClick={handleContinue}>
            <span>Continue to Sign In</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <line x1="5" y1="12" x2="19" y2="12" strokeWidth="2"/>
              <polyline points="12 5 19 12 12 19" strokeWidth="2"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Footer */}
      <div className="page-footer">
        <p>Powered by Yesod • Bringing communities together in prayer</p>
      </div>
    </div>
  );
}
